import { DocumentsSignatureStatusEnum } from 'generated/resources/graphql'

import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { apiClient } from 'modules/core/lib/mobx-rest'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'

import {
  DOCUMENT_SIGNATURE_BOUNCED_EMAIL,
  DOCUMENT_SIGNATURE_CANCELLED,
  DOCUMENT_SIGNATURE_COMPLETED,
  DOCUMENT_SIGNATURE_DECLINED,
  DOCUMENT_SIGNATURE_ERROR,
  DOCUMENT_SIGNATURE_EXPIRED,
  DOCUMENT_SIGNATURE_PARTIALLY_SIGNED,
  DOCUMENT_SIGNATURE_PENDING,
  ESIGNATURE_FILE_TYPE,
  LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT,
  SPACES,
} from './constants'

export function isPublicCompanyDocument(document: Document) {
  return document.space === SPACES.COMPANY_PUBLIC
}

function canUpdateDocument(document: Document) {
  return document.permissionUpdate
}

export function isPendingToFill(document: Document): boolean {
  return document.status === 'to_fill'
}

export function isTemplate(document: Document) {
  return !!document.templateId || document.fromTemplate
}

export function canUpdateDocuments(documents: Document[]) {
  return documents.every(canUpdateDocument)
}

export function isPendingSignature(document: Document) {
  return document.signatureStatus === 'pending' || document.signatureStatus === 'partially_signed'
}

export function isDeclinedSignatures(document: Document) {
  return document.signatureStatus === 'declined'
}

export function isPendingSignatures(document: Document) {
  return document.signatureStatus === 'pending'
}

export function isCancelledSignatures(document: Document) {
  return document.signatureStatus === 'cancelled'
}

export function isBouncedSignatures(document: Document) {
  return document.signatureStatus === 'bounced_email'
}

export function isPartiallySignedSignatures(document: Document) {
  return document.signatureStatus === 'partially_signed'
}

export function isErrorSignatures(document: Document) {
  return document.signatureStatus === 'error'
}

export function isExpiredSignatures(document: Document) {
  return document.signatureStatus === 'expired'
}

export function isCompanyInternalSpace(space: string) {
  return space === SPACES.COMPANY_INTERNAL
}

export function isCompanyPublicSpace(space: string) {
  return space === SPACES.COMPANY_PUBLIC
}

export function isCompanySpace(space: string) {
  return isCompanyInternalSpace(space) || isCompanyPublicSpace(space)
}

export function canFill(document: Document, employeeId: number) {
  return isPendingToFill(document) && document.employee?.id === employeeId
}

export function isInTrashBin(document: Document): boolean {
  return document.deletedAt !== undefined && document.deletedAt !== null
}

export function isSignable(document: Document): boolean {
  return document.contentType === ESIGNATURE_FILE_TYPE
}

export function downloadDocuments(ids: number[]) {
  return apiClient().post(
    '/documents/download_documents',
    { ids: ids.join(',') },
    { responseType: 'blob' }
  )
}

export function setSubfoldersCountForSkeletons(nSubfolders: number, folderId: number | undefined) {
  try {
    const folderSubfolders = JSON.parse(
      SafeLocalStorage.getItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, '[]') || '[]'
    )

    const selectedFolder = folderSubfolders.filter(
      (folder: { id: number; n: number }) => folder.id === folderId
    )[0]

    if (selectedFolder) {
      selectedFolder.n = nSubfolders
    } else {
      folderSubfolders.push({ id: folderId, n: nSubfolders })
    }

    SafeLocalStorage.setItem(
      LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT,
      JSON.stringify(folderSubfolders)
    )
  } catch (e) {
    SafeLocalStorage.setItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, JSON.stringify([]))
  }
}

export function getSubfoldersCountForSkeletons(folderId: number | undefined) {
  try {
    if (folderId) {
      const folderSubfolders = JSON.parse(
        SafeLocalStorage.getItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, '[]') || '[]'
      )

      return (
        folderSubfolders.filter((folder: { id: number; n: number }) => folder.id === folderId)[0]
          .n ?? 7
      )
    }
  } catch (e) {
    SafeLocalStorage.setItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, JSON.stringify([]))
  }
  return 7
}

export function documentSignatureStatusLabel(status: DocumentsSignatureStatusEnum) {
  switch (status) {
    case 'pending':
      return DOCUMENT_SIGNATURE_PENDING()
    case 'partially_signed':
      return DOCUMENT_SIGNATURE_PARTIALLY_SIGNED()
    case 'completed':
      return DOCUMENT_SIGNATURE_COMPLETED()
    case 'declined':
      return DOCUMENT_SIGNATURE_DECLINED()
    case 'bounced_email':
      return DOCUMENT_SIGNATURE_BOUNCED_EMAIL()
    case 'cancelled':
      return DOCUMENT_SIGNATURE_CANCELLED()
    case 'error':
      return DOCUMENT_SIGNATURE_ERROR()
    case 'expired':
      return DOCUMENT_SIGNATURE_EXPIRED()
    default:
      return ''
  }
}
